
import React from 'react';
import ReactDOM from 'react-dom';
import util from 'Utils';
import { Provider } from 'react-redux';
import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import createHistory from 'history/createBrowserHistory';
import router from 'Middleware/router';
import categoryIndicator from 'Middleware/categoryIndicator';
import reducers from 'Reducers';
import defaultState from 'Config/defaultState';
import { LOCATION_CHANGE, RESET_DATA } from 'Config/types';
import { actionPrintMode } from 'Actions';
import App from 'Components/app';
import qs from 'qs';
import {
  browserSupportsAllFeatures,
  loadScript,
} from './loadPolyfills';

const { getObjectFromString } = util.query;
const { hashString } = util.string;

if (browserSupportsAllFeatures()) {
  main();
} else {
  let scriptDomain;
  switch (window.location.hostname) {
    case 'kff-org-preprod.go-vip.co':
    case 'kff-org-develop.go-vip.co':
      scriptDomain = 'https://kff-cdr-staging.herokuapp.com';
      break;
    case 'kaiser-family-foundation.alley.test':
      scriptDomain = 'http://localhost:8080';
      break;
    default:
      scriptDomain = 'https://kff-cdr.herokuapp.com';
      break;
  }
  loadScript(`${scriptDomain}/static/polyfills.bundle.js`, main);
}

function main() {
  // Create a history (using browser history)
  const history = createHistory();

  let initialState = { ...defaultState };

  if ('#print' === window.location.hash) {
    const localStore = JSON.parse(window.localStorage.getItem('cds-print'));
    if (localStore) {
      initialState = { ...localStore };
    }
  } else {
    window.localStorage.removeItem('cds-print');
  }

  // Abstracted setup logic invoked conditionally in an 'if' statement below.
  const setup = () => {
    // On intial load, parse `category` to build
    // initial collection of selected data
    const initialQuery = getObjectFromString(window.location.search);

    if (initialQuery.category) {
      const selectedCategories =
        Object.keys(initialState.data.categories).reduce((acc, curr) => {
          const { slug } = initialState.data.categories[curr];
          if (-1 !== initialQuery.category.indexOf(slug)) {
            acc.push(parseInt(curr, 10));
          }
          return acc;
        },
      []);

      initialState.data.selectedCategories = [...new Set(
        (initialState.data.selectedCategories || []).concat(selectedCategories)
      )];
    }

    // See https://stackoverflow.com/questions/35622588/how-to-reset-the-state-of-a-redux-store/35641992#35641992
    const rootReducer = (state, action) => {
      if (RESET_DATA === action.type) {
        state = { ...initialState }; // eslint-disable-line no-param-reassign
      }
      return reducers(state, action);
    };

    // Get view param from query string.
    const viewParam = () => {
      // Get current view.
      const {
        view: view = '',
        '?view': viewQS = '', // If the first item.
      } = qs.parse(location.search);
      return view || viewQS;
    };

    // Get indicator param from query string.
    const indicatorParam = () => {
      // Get indicators from url.
      const {
        i: indicator = '',
        '?i': indicatorQS = '', // If the first item.
      } = qs.parse(location.search);
      return indicator || indicatorQS;
    };

    // Get region param from query string.
    const regionParam = () => {
      // Get regions from url.
      const {
        g: region = '',
        '?g': regionQS = '', // If the first item.
      } = qs.parse(location.search);
      return region || regionQS;
    };

    // Handle rerouting app to "new" app.
    const resetApp = () => {
      // Get current view.
      const stage = viewParam();
      // Get indicators from url.
      const indicators = indicatorParam();
      // Get regions from url.
      const regions = regionParam();

      // Empty object.
      const params = {};

      // Reroute app if url changes.
      if ('3' !== stage) {
        params.stage = stage;
        if (indicators) {
          params.indicators = indicators.split('~').join(',');
        }
        if (regions) {
          params.regions = regions.toUpperCase().split('~').join(',');
        }
        window.location.href = `/statedata/custom/?${qs.stringify(params)}`; // phpcs:ignore WordPressVIPMinimum.JS.Window.location
      }

      // If on report and removes all regions, go back to step 2.
      if (0 === regions.length) {
        params.stage = 2;
        if (indicators) {
          params.indicators = indicators.split('~').join(',');
          params.regions = '';
        }
        window.location.href = `/statedata/custom/?${qs.stringify(params)}`; // phpcs:ignore WordPressVIPMinimum.JS.Window.location
      }

      // If on report and removes all indicators, go back to step 2.
      if (0 === indicators.length) {
        params.stage = 1;
        if (regions) {
          params.regions = regions.toUpperCase().split('~').join(',');
          params.indicators = '';
        }
        window.location.href = '/statedata/custom/'; // phpcs:ignore WordPressVIPMinimum.JS.Window.location
      }
    };

    const store = createStore(
      rootReducer,
      initialState,
      composeWithDevTools(
        applyMiddleware(
          thunk,
          // Middleware to handle deselecting indicators if categories deselected
          categoryIndicator,
          // setup router middleware to intercept and dispatch nav actions
          router(history)
        )
      )
    );

    // Set the store to a window object so ag-grid can use it...
    window.appJs = { ...window.appJs, store };

    // Subscribe to URL changes
    history.listen((location) => {
      store.dispatch({ type: LOCATION_CHANGE, payload: location });
      if (location.viewChanged) {
        window.scroll(0, 0);
      }
    });

    // Initial Load of URL into state
    if (history.location.search) {
      const stage = viewParam();
      const indicators = indicatorParam();
      const regions = regionParam();

      // Reset the app to the new location if we aren't creating a report.
      if ('3' !== stage || '' === stage || '' === regions || '' === indicators) {
        resetApp();
      } else {
        store.dispatch({
          type: LOCATION_CHANGE,
          payload: { search: decodeURIComponent(history.location.search) },
        });
      }
    }

    if ('#print' === window.location.hash) {
      store.dispatch(actionPrintMode());
    }

    // Make reducers hot reloadable, see http://stackoverflow.com/questions/34243684/make-redux-reducers-and-other-non-components-hot-loadable
    if (module.hot) {
      module.hot.accept('./reducers/', () => {
        store.replaceReducer(require('./reducers/').default); // eslint-disable-line global-require
      });
    }

    ReactDOM.render(
      <Provider store={store}>
        {/* ConnectedRouter will use the store from provider automagically */}
        <App />
      </Provider>,
      document.querySelector('[kff-cds]')
    );
  };

  /**
   * Builds category hash table lookup if `window.appJs` exists.
   * `appJs` is attached to the window by KFF multisite in production and develop/preprod envs.
   * Local env uses dummy data in `mockWPData.json` and proceeds directly to `setup()`.
   */
  if (window.appJs) {
    // Encrypt category names asynchronously into hashed strings.
    // Returns array of promises.
    const hashPromises =
      Object.entries(window.appJs.categorization.categories)
      .map((category) => hashString(category[1].name));

    // Resolve promise hashes and assign hash strings to appropriate categories.
    Promise.all(hashPromises).then((categoryHashes) => {
      Object.entries(window.appJs.categorization.categories)
        .forEach((category, index) => {
          category[1].categoryHash = categoryHashes[index]; // eslint-disable-line no-param-reassign
        });

      initialState.data = {
        ...initialState.data,
        ...window.appJs.categorization,
        options: window.appJs.options,
      };

      setup();
    });
  } else {
    setup();
  }
}



// WEBPACK FOOTER //
// ./app/main.js